////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/*
R: Taylor Legal Engineering Homepage
- Minimal landing page
- Animated aurora background, glowing monogram, shimmering gradient title
*/

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// DVT_EIS_TP: THIRD-PARTY IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// DVT_IIS: ANIMATIONS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

const orbDrift1 = keyframes`
  0%, 100% { transform: translate(-20%, -10%) scale(1); }
  33%      { transform: translate(15%, 20%) scale(1.15); }
  66%      { transform: translate(25%, -25%) scale(0.9); }
`;

const orbDrift2 = keyframes`
  0%, 100% { transform: translate(20%, 30%) scale(1); }
  50%      { transform: translate(-25%, -20%) scale(1.2); }
`;

const orbDrift3 = keyframes`
  0%, 100% { transform: translate(-15%, 25%) scale(0.95); }
  40%      { transform: translate(30%, -15%) scale(1.1); }
  80%      { transform: translate(-10%, -30%) scale(1); }
`;

const monogramGlow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 12px rgba(45, 212, 191, 0.35)) drop-shadow(0 0 32px rgba(20, 184, 166, 0.18)); }
  50%      { filter: drop-shadow(0 0 24px rgba(45, 212, 191, 0.6)) drop-shadow(0 0 48px rgba(20, 184, 166, 0.32)); }
`;

const titleShimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const lineGrow = keyframes`
  from { transform: scaleX(0); opacity: 0; }
  to   { transform: scaleX(1); opacity: 1; }
`;

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// DVT_IIS: STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

const Page = styled.main`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
  background:
    radial-gradient(ellipse at top, #0f1a2e 0%, #050810 50%, #02040a 100%);
  color: #e6edf6;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  will-change: transform;
`;

const Orb1 = styled(Orb)`
  width: 60vw;
  height: 60vw;
  top: 10%;
  left: 10%;
  background: radial-gradient(circle, rgba(20, 184, 166, 0.35) 0%, rgba(20, 184, 166, 0) 70%);
  animation: ${orbDrift1} 22s ease-in-out infinite;
`;

const Orb2 = styled(Orb)`
  width: 50vw;
  height: 50vw;
  bottom: 5%;
  right: 5%;
  background: radial-gradient(circle, rgba(45, 212, 191, 0.28) 0%, rgba(45, 212, 191, 0) 70%);
  animation: ${orbDrift2} 28s ease-in-out infinite;
`;

const Orb3 = styled(Orb)`
  width: 45vw;
  height: 45vw;
  top: 40%;
  left: 35%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.18) 0%, rgba(99, 102, 241, 0) 70%);
  animation: ${orbDrift3} 35s ease-in-out infinite;
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(148, 163, 184, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.05) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
  pointer-events: none;
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, transparent 50%, rgba(2, 4, 10, 0.85) 100%);
  pointer-events: none;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const MonogramWrap = styled.div`
  animation: ${fadeUp} 1s ease-out 0.1s backwards, ${monogramGlow} 4s ease-in-out 1.1s infinite;
`;

const Monogram = styled.svg`
  width: 84px;
  height: 84px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(36px, 6vw, 64px);
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.05;
  background: linear-gradient(
    90deg,
    #e6edf6 0%,
    #2dd4bf 25%,
    #e6edf6 50%,
    #2dd4bf 75%,
    #e6edf6 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${fadeUp} 1s ease-out 0.3s backwards, ${titleShimmer} 8s linear 1.3s infinite;
`;

const AccentLine = styled.div`
  width: 80px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #2dd4bf, transparent);
  transform-origin: center;
  animation: ${lineGrow} 0.9s ease-out 0.7s backwards;
`;

const Tagline = styled.p`
  margin: 0;
  font-size: clamp(14px, 1.6vw, 16px);
  font-weight: 300;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  animation: ${fadeUp} 1s ease-out 0.9s backwards;
`;

// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════
// O_1: COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════

export const TLE_Home_SRC_Homepage_Lobby_Pod = () => {
  return (
    <Page>
      <Orb1 />
      <Orb2 />
      <Orb3 />
      <Grid />
      <Vignette />
      <Inner>
        <MonogramWrap>
          <Monogram viewBox="0 0 48 48" fill="none" aria-hidden="true">
            <defs>
              <linearGradient id="tleGrad" x1="2" y1="2" x2="46" y2="46" gradientUnits="userSpaceOnUse">
                <stop stopColor="#14b8a6" />
                <stop offset="1" stopColor="#2dd4bf" />
              </linearGradient>
            </defs>
            <rect x="6" y="6" width="27" height="6" fill="url(#tleGrad)" />
            <rect x="15.5" y="6" width="7" height="36" fill="url(#tleGrad)" />
            <rect x="22.5" y="20" width="11" height="6" fill="url(#tleGrad)" />
            <rect x="22.5" y="36" width="16" height="6" fill="url(#tleGrad)" />
          </Monogram>
        </MonogramWrap>
        <Title>Taylor Legal Engineering</Title>
        <AccentLine />
        <Tagline>A tinkering playground</Tagline>
      </Inner>
    </Page>
  );
};

export default TLE_Home_SRC_Homepage_Lobby_Pod;
